import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AuthOtpResponse,
  SendEmailCodeResponse,
  SendSmsCodeResponse,
  HStreamCallbackResponse,
  IdentityPrefillStartResponse,
  IdentitySubmitOtpResponse,
  PrefillClientPayload,
} from 'api/AuthApi';
import {
  authOtp,
  sendEmailCode,
  sendSmsCode,
  sendSmsCodeForApplicationId,
  hstreamCallback,
  identityPrefill,
  identitySubmitOtp,
} from 'thunks';

export interface AuthData {
  phoneId?: string;
  emailId?: string;
  sessionToken?: string;
  idToken?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
  hstreamUserInfo?: HStreamCallbackResponse;
  prefillSessionId?: string;
  crsUserId?: string;
  prefillContinuationToken?: string;
  emailPresent?: boolean;
  prefill?: PrefillClientPayload;
}

const initialState: AuthData = {};

const applyPrefillSuccess = (
  state: AuthData,
  payload: {
    sessionToken: string;
    emailPresent: boolean;
    crsUserId: string;
    prefillContinuationToken: string;
    prefill: PrefillClientPayload;
  },
) => {
  state.sessionToken = payload.sessionToken;
  state.emailPresent = payload.emailPresent;
  state.crsUserId = payload.crsUserId;
  state.prefillContinuationToken = payload.prefillContinuationToken;
  state.prefill = payload.prefill;
};

const auth = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearPrefillData: (state) => {
      state.prefillSessionId = undefined;
      state.crsUserId = undefined;
      state.prefillContinuationToken = undefined;
      state.emailPresent = undefined;
      state.prefill = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendSmsCode.fulfilled, (state, { payload }: PayloadAction<SendSmsCodeResponse>) => {
      state.phoneId = payload.phoneId;
    });
    builder.addCase(sendSmsCodeForApplicationId.fulfilled, (state, { payload }: PayloadAction<SendSmsCodeResponse>) => {
      state.phoneId = payload.phoneId;
    });
    builder.addCase(sendEmailCode.fulfilled, (state, { payload }: PayloadAction<SendEmailCodeResponse>) => {
      state.emailId = payload.emailId;
    });
    builder.addCase(authOtp.fulfilled, (state, { payload }: PayloadAction<AuthOtpResponse>) => {
      if (payload.verified) {
        state.sessionToken = payload.sessionToken;
      }
    });
    builder.addCase(identityPrefill.fulfilled, (state, { payload }: PayloadAction<IdentityPrefillStartResponse>) => {
      state.prefillSessionId = payload.prefillSessionId;
      if (
        payload.verified &&
        payload.sessionToken &&
        payload.prefill &&
        payload.crsUserId &&
        payload.prefillContinuationToken
      ) {
        applyPrefillSuccess(state, {
          sessionToken: payload.sessionToken,
          emailPresent: Boolean(payload.emailPresent),
          crsUserId: payload.crsUserId,
          prefillContinuationToken: payload.prefillContinuationToken,
          prefill: payload.prefill,
        });
      }
    });
    builder.addCase(identitySubmitOtp.fulfilled, (state, { payload }: PayloadAction<IdentitySubmitOtpResponse>) => {
      if (payload.verified) {
        applyPrefillSuccess(state, payload);
      }
    });
    builder.addCase(hstreamCallback.fulfilled, (state, { payload }: PayloadAction<HStreamCallbackResponse>) => {
      state.idToken = payload.tokens.idToken;
      state.accessToken = payload.tokens.accessToken;
      state.refreshToken = payload.tokens.refreshToken;
      state.expiresIn = payload.tokens.expiresIn;
      state.tokenType = payload.tokens.tokenType;
      state.hstreamUserInfo = payload;

      // Set sessionToken from idToken for compatibility with existing auth flow
      state.sessionToken = payload.tokens.idToken;
    });
  },
});

export const { clearPrefillData } = auth.actions;

export default auth.reducer;
